import { useEffect, useRef } from "react";

/** The element focus should return to when a modal closes: the one that
 *  had it when the modal opened, or — when that element has since left
 *  the page (a dropdown item unmounts with its menu) — the most recently
 *  focused element that is still mounted; null when none is. */
export function pickRestoreTarget<T extends { isConnected: boolean }>(
  previous: T | null,
  history: readonly T[],
): T | null {
  if (previous?.isConnected) return previous;
  for (let i = history.length - 1; i >= 0; i--) {
    const candidate = history[i] as T;
    if (candidate.isConnected) return candidate;
  }
  return null;
}

// The last few elements that held focus, oldest first — the fallback for
// a restore whose trigger is gone. One document listener, shared, and
// listening from module load rather than from the first modal's open:
// the very first modal's trigger must already be on record when that
// trigger unmounts with its menu.
const HISTORY_SIZE = 8;
const focusHistory: HTMLElement[] = [];
let listening = false;
function ensureFocusHistory() {
  if (listening || typeof document === "undefined") return;
  listening = true;
  document.addEventListener("focusin", (e) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    focusHistory.push(target);
    if (focusHistory.length > HISTORY_SIZE) focusHistory.shift();
  });
}
ensureFocusHistory();

/**
 * Focus a modal when it opens and restore focus to the previously focused
 * element when it closes — required for keyboard and screen-reader users.
 * Attach the returned ref to the dialog element (with tabIndex={-1}). A
 * control inside the modal that autofocuses keeps its focus; otherwise
 * the card itself takes focus, so that Tab walks the modal top to bottom
 * from its first control — the close button in the header, then the body,
 * then the footer's Cancel and confirm — and the trap (focusTrap) wraps
 * from there. The restore runs from the effect's cleanup, so it covers a
 * modal whose isOpen flips false and one the page unmounts on close alike.
 */
export function useModalFocus(isOpen: boolean) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    // The history as it stood before the modal took focus: its own
    // autofocused control, when it has one, is the newest entry.
    const history = focusHistory.slice(0, -1);
    const openedOn = window.location.pathname;
    const modal = modalRef.current;
    if (modal && !modal.contains(document.activeElement)) modal.focus();
    return () => {
      // The cleanup runs in the commit that unmounts the modal, after a
      // navigation the same answer started has already changed the URL.
      if (!restoresFocusOnClose(openedOn, window.location.pathname)) return;
      pickRestoreTarget(previous, history)?.focus();
    };
  }, [isOpen]);

  return modalRef;
}

/** Whether a closing modal hands focus back to its trigger: yes while
 *  the page is the one it opened on, no once the app has navigated away
 *  (the leave guard's Continue, a delete that returns to its list) — the
 *  trigger was the way out, and the new page starts with no focus, as
 *  after a plain click. The pathname alone counts: the studio's
 *  ?workspace= switch stays on the page and keeps its restore. */
export function restoresFocusOnClose(
  openedOn: string,
  closedOn: string,
): boolean {
  return openedOn === closedOn;
}
